import React from 'react'
import {useState} from 'react'
import {GoogleMap, Marker, Polygon} from 'react-google-maps'
import Button from '@components/Button'
import MapsProvider from '@components/MapsProvider'
import Loading from '@components/SuspenseLoading/Loading'
import {getStoresZones_cached_stores} from '@data/queries/components/__generated__/getStoresZones_cached'
import {deliveryZonesQuery} from '@data/queries/components/deliveryZone'
import getFileURL from '@helpers/misc/getFileURL'
import isServerSide from '@helpers/misc/isServerSide'
import getPathname from '@helpers/router/getPathname'
import useWebsiteId from '@hooks/useWebsiteId'
import useQuery from '@packages/apollo-hooks/lib/useApolloQuery'
import Container from '@packages/justo-parts/lib/components/Container'
import flattenDeep from 'lodash/flattenDeep'
import {useRouter} from 'next/router'

import styles from './styles.module.css'

export interface DeliveryZoneProps {
  stores: any
  color: string
  icon: any
  ssrStores?: getStoresZones_cached_stores
}

export default function DeliveryZone(props: DeliveryZoneProps) {
  const router = useRouter()
  const [show, setShow] = useState(!['/', '/pedir', '/checkout'].includes(getPathname(router)))
  const websiteId = useWebsiteId()

  const {stores: queriedStores} = useQuery({
    query: deliveryZonesQuery,
    variables: {websiteId},
    omit: isServerSide(),
    partial: true
  })

  const stores = queriedStores ? queriedStores : props.ssrStores
  if (isServerSide() && !props.ssrStores) {
    console.warn(
      'Could not fetch stores while doing SSR. This should be fixed for an optimized experience'
    )
    return <Loading />
  }

  const getStores = () => {
    if (!stores) return []
    return stores.items
      .filter(store => store.acceptDelivery)
      .filter(store => store.address && store.address.location)
  }

  const setCenter = map => {
    if (!map) return
    const points = flattenDeep(
      getStores().map(store => store.zones.map(zone => zone.deliveryLimits))
    )
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'maps' does not exist on type 'typeof goo... Remove this comment to see the full error message
    const {LatLngBounds} = global.google.maps
    const bounds = new LatLngBounds()
    for (const point of points) {
      bounds.extend(point)
    }
    map.fitBounds(bounds)
  }

  const renderAreas = () => {
    const options = {
      strokeWeight: 1,
      fillOpacity: 0.2,
      fillColor: props.color,
      strokeColor: props.color
    }
    return getStores().map(store => {
      return (
        <React.Fragment key={`store_${store._id}`}>
          {store.zones.map(zone => (
            <Polygon paths={zone.deliveryLimits} key={`polygon_${zone._id}`} options={options} />
          ))}
        </React.Fragment>
      )
    })
  }

  const renderStores = () => {
    return getStores().map(store => {
      const label = {
        fontSize: '16px',
        fontWeight: 'bold'
      }
      const iconSize = 25
      const icon = {
        url: props.icon ? getFileURL(props.icon) : '/images/store-in-map.svg',
        scaledSize: {width: iconSize, height: iconSize},
        labelOrigin: {x: iconSize / 2, y: -10},
        anchor: {x: iconSize / 2, y: iconSize / 2}
      }
      return (
        <Marker
          icon={icon}
          label={label}
          position={store.address.location}
          key={`marker_${store._id}`}
        />
      )
    })
  }

  const getCenter = () => {
    const count = getStores().length
    return getStores().reduce(
      (last, store) => {
        const lat = store.address.location.lat / count
        const lng = store.address.location.lng / count
        return {lat: last.lat + lat, lng: last.lng + lng}
      },
      {lat: 0, lng: 0}
    )
  }

  if (!stores) return null
  if (!getStores().length) return null
  return (
    <div className={styles.container}>
      <Container>
        <div className={styles.title}>Cobertura del despacho</div>
        {show ? (
          <MapsProvider>
            <GoogleMap
              defaultCenter={getCenter()}
              // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
              fullscreenControl={false}
              clickableIcons={false}
              defaultZoom={15}
              ref={setCenter}>
              {renderAreas()}
              {renderStores()}
            </GoogleMap>
          </MapsProvider>
        ) : (
          <Button onClick={() => setShow(true)}>Mostrar mapa</Button>
        )}
      </Container>
    </div>
  )
}
